import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * GTM loader. Injects Google Tag Manager only when REACT_APP_GTM_ID is set.
 * Also pushes a `page_view` event on every SPA route change so GTM/GA4 can
 * capture SPA navigations without extra config.
 *
 * All Phase-1 conversion events (project_estimate_started, _step_completed,
 * _submitted, file_uploaded, book_consultation_clicked) already push to
 * window.dataLayer via /src/lib/analytics.js — this component ensures GTM is
 * actually loaded to pick them up. It does NOT re-fire any event, so there is
 * no risk of duplicate tracking.
 */
export default function GTM() {
  const id = process.env.REACT_APP_GTM_ID;
  const location = useLocation();

  // Ensure window.dataLayer exists on every page-load, regardless of whether
  // GTM itself is enabled. This lets our analytics event helpers safely push
  // and keeps the site "GTM-ready" even before the container ID is set.
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
    }
  }, []);

  // Load GTM once on mount if the container ID is present.
  useEffect(() => {
    if (!id) return;
    if (window.__GTM_LOADED__) return;

    // Standard GTM snippet — dataLayer already exists (used by our events).
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    window.__GTM_LOADED__ = true;

    // Crawler snapshots already carry the GTM script/noscript from the first render — don't add a second copy.
    if (document.querySelector('script[src*="googletagmanager.com/gtm.js"]')) return;

    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
    document.head.appendChild(s);

    // Fallback noscript iframe for browsers with JS disabled.
    const ns = document.createElement("noscript");
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${id}`;
    iframe.height = "0";
    iframe.width = "0";
    iframe.style.cssText = "display:none;visibility:hidden";
    ns.appendChild(iframe);
    document.body.insertBefore(ns, document.body.firstChild);
  }, [id]);

  // SPA page-view tracking — one page_view per route change. Fires regardless
  // of whether GTM is loaded, so a future GA4 direct-install still gets SPA
  // navigation events without extra wiring.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "page_view",
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);

  return null;
}
